// Vad ska stå i "Belopp" för en bokning i spelarens /bookings?
//
// Bakgrund (audit-reports/belopp-mina-bokningar/fas0-rapport.md): tabellen läste
// `booking.total_price` — ett fält som inte finns (kolumnen heter `price_total`).
// Uttrycket var alltid undefined, så ALLA rader visade "–", även Betald. Mobilvyn
// läste i stället betalraden och visade rätt belopp; samma sida gav alltså olika
// svar beroende på skärmbredd. Den här helpern är nu enda källan för båda.
//
// Källordningen är inte godtycklig — `bookings.price_total` är opålitlig (L51:
// 0 för v2-bokningar; på AP bär betalraderna 142 779 kr mot price_totals 22 000).
// Betalraden går därför före, men price_total behövs ändå som sista utväg: 1063
// bokningar har pris utan betalrad och skulle annars visa "–".
//
// GRÄNS mot C3-spåret: `excludeMatchiPayments` hör till INTÄKTSREDOVISNING och
// får aldrig användas här. Kundens kvittobild ska visa vad kunden betalat,
// oavsett metod. Den här filen känner medvetet inte till MATCHi.

import type { Money } from "@/utils/formatPrice";

/** Betalstatusar som representerar pengar kunden faktiskt lagt ut. */
const SETTLED_STATUSES = ["paid", "manual", "refunded"] as const;

export interface BookingAmountSource {
  id: string;
  booking_group_id?: string | null;
  start_time: string;
  currency?: string | null;
  custom_price?: number | null;
  custom_price_minor?: number | null;
  custom_price_note?: string | null;
  price_total?: number | null;
  price_total_minor?: number | null;
}

export interface PaymentAmountSource {
  booking_id: string;
  user_id?: string | null;
  status?: string | null;
  amount?: number | null;
  amount_minor?: number | null;
  currency?: string | null;
}

export type BookingAmountDisplay =
  /** Visa ett belopp. `note` är custom_price_note när sådan finns. */
  | { kind: "amount"; money: Money; note: string | null }
  /** Syskon i en grupp där betalningen ligger på primärraden. */
  | { kind: "groupSibling" }
  /** Ingen beloppsdata alls — rendera "–". */
  | { kind: "none" };

/** Öre ur en dual-state-rad. `null` när båda fälten saknas (0 är ett giltigt värde). */
function toMinor(minor: number | null | undefined, major: number | null | undefined): number | null {
  if (typeof minor === "number" && Number.isFinite(minor)) return minor;
  if (typeof major === "number" && Number.isFinite(major)) return Math.round(major * 100);
  return null;
}

function isSettled(p: PaymentAmountSource): boolean {
  return SETTLED_STATUSES.includes((p.status ?? "") as (typeof SETTLED_STATUSES)[number]);
}

/** Summerar öre. Returnerar null när listan är tom — 0 kr är ett riktigt belopp, inte "saknas". */
function sumMinor(rows: readonly PaymentAmountSource[]): number | null {
  if (rows.length === 0) return null;
  return rows.reduce((s, p) => s + (toMinor(p.amount_minor, p.amount) ?? 0), 0);
}

function currencyOf(rows: readonly PaymentAmountSource[], fallback: string | null | undefined): string {
  return rows.find((p) => p.currency)?.currency ?? fallback ?? "SEK";
}

/**
 * Gruppens primärrad = lägsta (start_time, id). Schemat saknar primär-markör
 * (enda grupp-kolumnen är `booking_group_id`), så ordningen definieras här —
 * deterministiskt, så primären inte hoppar mellan renderingar.
 */
function isGroupPrimary(booking: BookingAmountSource, siblings: readonly BookingAmountSource[]): boolean {
  const primary = [...siblings].sort((a, b) => {
    const t = a.start_time.localeCompare(b.start_time);
    return t !== 0 ? t : a.id.localeCompare(b.id);
  })[0];
  return primary?.id === booking.id;
}

/**
 * Beloppet som ska visas för `booking`.
 *
 * Källordning — första träffen vinner:
 *   1. `custom_price` (manuellt satt pris, högst auktoritet — behåller sin note)
 *   2. användarens EGNA betalrader på bokningen (kvittobilden)
 *   3. gruppen har betalrader → primären visar gruppens summa, syskon "ingår i gruppbokning"
 *   4. bokningens betalrader oavsett användare
 *   5. `price_total` när > 0
 *   6. inget → "–"
 *
 * `allBookings` behövs bara för grupp-fallet; utan den hoppas steg 3 över.
 */
export function resolveBookingDisplayAmount(
  booking: BookingAmountSource,
  payments: readonly PaymentAmountSource[],
  userId: string | null | undefined,
  allBookings?: readonly BookingAmountSource[],
): BookingAmountDisplay {
  // 1. Manuellt satt pris.
  const customMinor = toMinor(booking.custom_price_minor, booking.custom_price);
  if (customMinor != null) {
    return {
      kind: "amount",
      money: { valueMinor: customMinor, currency: booking.currency ?? "SEK" },
      note: booking.custom_price_note ?? null,
    };
  }

  const ownRows = payments.filter((p) => p.booking_id === booking.id && isSettled(p));

  // 2. Användarens egna betalrader.
  if (userId) {
    const mine = ownRows.filter((p) => p.user_id === userId);
    const minor = sumMinor(mine);
    if (minor != null) {
      return {
        kind: "amount",
        money: { valueMinor: minor, currency: currencyOf(mine, booking.currency) },
        note: null,
      };
    }
  }

  // 3. Grupp: betalningen ligger typiskt på EN rad. Visa den på primären och
  //    markera syskonen — annars ser 5 rader à 500 kr ut som 2 500 kr.
  //    Regeln gäller bara när gruppen faktiskt HAR betalrader; annars är varje
  //    bokning självständigt prissatt och faller igenom till sitt eget belopp.
  if (booking.booking_group_id && allBookings) {
    const siblings = allBookings.filter((b) => b.booking_group_id === booking.booking_group_id);
    const siblingIds = new Set(siblings.map((b) => b.id));
    const groupRows = payments.filter((p) => siblingIds.has(p.booking_id) && isSettled(p));

    if (groupRows.length > 0) {
      if (!isGroupPrimary(booking, siblings)) return { kind: "groupSibling" };
      const minor = sumMinor(groupRows);
      if (minor != null) {
        return {
          kind: "amount",
          money: { valueMinor: minor, currency: currencyOf(groupRows, booking.currency) },
          note: null,
        };
      }
    }
  }

  // 4. Bokningens betalrader oavsett användare (t.ex. bokad av någon annan).
  const anyMinor = sumMinor(ownRows);
  if (anyMinor != null) {
    return {
      kind: "amount",
      money: { valueMinor: anyMinor, currency: currencyOf(ownRows, booking.currency) },
      note: null,
    };
  }

  // 5. Bokningens eget pris — enda källan för de bokningar som saknar betalrad.
  //    Endast > 0: ett nollat price_total betyder "inget pris satt", inte "gratis".
  const bookingMinor = toMinor(booking.price_total_minor, booking.price_total);
  if (bookingMinor != null && bookingMinor > 0) {
    return {
      kind: "amount",
      money: { valueMinor: bookingMinor, currency: booking.currency ?? "SEK" },
      note: null,
    };
  }

  return { kind: "none" };
}

/** Sorteringsnyckel för Belopp-kolumnen. Saknat belopp sorteras som 0. */
export function bookingDisplayAmountMinor(
  booking: BookingAmountSource,
  payments: readonly PaymentAmountSource[],
  userId: string | null | undefined,
  allBookings?: readonly BookingAmountSource[],
): number {
  const r = resolveBookingDisplayAmount(booking, payments, userId, allBookings);
  return r.kind === "amount" ? r.money.valueMinor : 0;
}
